import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Flex, HStack, IconButton, Image, Slider, SliderFilledTrack, SliderThumb,
  SliderTrack, Stack, Text, useToast,
} from '@chakra-ui/react';
import { RootState } from '../redux/store';
import {
  expand, next, onEnded, pause, play, prev, seek, setDuration, setProgress, setVolume,
  updateTrack, markUnavailable,
} from '../redux/features/playerSlice';
import { mixgenieApi } from '../redux/services/mixgenie';

/**
 * Global sticky media player. One <audio> per app, controlled entirely by
 * the `player` Redux slice. Renders a compact control strip pinned to the
 * bottom of PublicShell — SoundCloud pattern. Hidden when nothing is
 * queued so pages aren't shifted up for no reason.
 *
 * Also wires the OS `mediaSession` metadata + action handlers so lock-screen
 * playback controls (and Bluetooth headphone buttons) work when the site
 * is installed as a PWA.
 */
export function GlobalAudioPlayer() {
  const dispatch = useDispatch();
  const toast = useToast();
  const { queue, index, isPlaying, progress, duration, volume, repeatMode } = useSelector((s: RootState) => s.player);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const track = index >= 0 ? queue[index] : null;
  // Retry bookkeeping — Mureka signed URLs expire in 24h. On <audio>
  // error we (a) refetch the job so the backend can re-sign, (b) if
  // the fresh URL differs apply it, otherwise append a cache-buster so
  // the element treats it as a new source (defeats any CDN 4xx cache),
  // (c) retry play. One retry — if it still fails the file is gone;
  // we mark the track unavailable and auto-skip to the next one.
  const attemptsRef = useRef<Map<string, number>>(new Map());
  const MAX_ATTEMPTS = 1;

  // Clear stale attempts whenever the audio successfully starts — the
  // user's track is playing so any prior failures are no longer relevant.
  const clearAttempts = (id: string | undefined) => {
    if (id) attemptsRef.current.delete(id);
  };

  const bustUrl = (url: string): string => {
    if (!url) return url;
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}_r=${Date.now()}`;
  };

  const stripBust = (url: string): string => {
    if (!url) return url;
    return url.replace(/([?&])_r=\d+&?/, (_m, p1) => (p1 === '&' ? '&' : '')).replace(/[?&]$/, '');
  };

  const handleAudioError = async () => {
    const a = audioRef.current;
    const t = track;
    if (!t) return;
    const mediaErr = a?.error;
    // MediaError codes: 1 aborted, 2 network, 3 decode, 4 src not supported.
    // Log so we can spot which failure mode is hitting users when
    // support asks — the toast is user-facing but this is dev-facing.
    // eslint-disable-next-line no-console
    console.warn('[GlobalAudioPlayer] audio error', {
      code: mediaErr?.code,
      message: mediaErr?.message,
      trackId: t.id,
      url: t.audioUrl,
    });

    const attempt = (attemptsRef.current.get(t.id) || 0) + 1;
    attemptsRef.current.set(t.id, attempt);
    if (attempt > MAX_ATTEMPTS) {
      // File really isn't available — mark it and auto-skip. The UI
      // reads `player.unavailableIds` and renders a greyed "unavailable"
      // state on this row so the user knows why we moved on.
      dispatch(markUnavailable(t.id));
      const hasNext = index >= 0 && index < queue.length - 1;
      if (hasNext) {
        dispatch(next());
      } else {
        dispatch(pause());
        toast({
          title: 'Reached the end',
          description: 'The remaining songs in the queue are unavailable.',
          status: 'info',
          duration: 3000,
        });
      }
      return;
    }

    let nextUrl = t.audioUrl;
    try {
      const action = mixgenieApi.endpoints.getJob.initiate(t.id, { forceRefetch: true });
      const result: any = await (dispatch as any)(action).unwrap();
      const fresh = result?.data?.outputAudioUrl || result?.data?.outputUrl
        || result?.outputAudioUrl || result?.outputUrl;
      if (fresh) nextUrl = fresh;
    } catch {
      // Refetch failed (offline, 5xx). Fall through and still cache-bust +
      // retry — the audio might work on a plain reload.
    }

    // If URL unchanged (backend saw it as fresh), append a cache-buster
    // so the <audio> element treats it as a new src and reloads it.
    const currentClean = stripBust(t.audioUrl);
    const nextClean = stripBust(nextUrl);
    const busted = nextClean === currentClean ? bustUrl(nextClean) : nextClean;
    dispatch(updateTrack({ id: t.id, patch: { audioUrl: busted } }));
    // Sync effect picks up the new url + isPlaying is still true → play()
    // fires again on canplay. No explicit action needed here.
  };

  // --- Sync <audio> element to Redux state --------------------------------
  //
  // Consolidated effect. The previous implementation had two effects
  // (one to set src+load, one to call play). When both fired in the
  // same tick (row-tap → setQueue changes both track.id AND isPlaying),
  // play() raced load() and rejected with AbortError → we dispatched
  // pause() → nothing played. The fix: whenever src changes, defer
  // play() to the `canplay` event so the element is actually ready.

  useEffect(() => {
    const a = audioRef.current;
    if (!a || !track) return;

    const needsLoad = a.src !== track.audioUrl;
    if (needsLoad) {
      a.src = track.audioUrl;
      a.load();
    }

    if (isPlaying) {
      if (needsLoad || a.readyState < 2 /* HAVE_CURRENT_DATA */) {
        // Wait for the media pipeline to be ready before calling play().
        // Some browsers (Safari) throw AbortError otherwise.
        const start = () => {
          a.play().catch(() => { dispatch(pause()); });
          a.removeEventListener('canplay', start);
        };
        a.addEventListener('canplay', start);
        return () => a.removeEventListener('canplay', start);
      }
      a.play().catch(() => { dispatch(pause()); });
    } else {
      a.pause();
    }
  }, [track?.id, track?.audioUrl, isPlaying, dispatch]);

  // Repeat-one: onEnded fires, slice keeps the same index, but the audio
  // element is at duration and won't auto-restart. Reset currentTime + play.
  useEffect(() => {
    const a = audioRef.current;
    if (!a || !track || repeatMode !== 'one') return;
    if (progress === 0 && isPlaying && a.currentTime > 0.5) {
      a.currentTime = 0;
      a.play().catch(() => { /* autoplay blocked */ });
    }
  }, [progress, isPlaying, repeatMode, track]);

  useEffect(() => {
    const a = audioRef.current;
    if (a) a.volume = volume;
  }, [volume]);

  // --- MediaSession — lock-screen + headphone buttons ---------------------

  useEffect(() => {
    if (!('mediaSession' in navigator) || !track) return;
    navigator.mediaSession.metadata = new window.MediaMetadata({
      title: track.title || 'MixGenie',
      artist: track.artist || 'MixGenie',
      album: 'MixGenie',
      artwork: track.coverUrl ? [{ src: track.coverUrl, sizes: '512x512', type: 'image/png' }] : undefined,
    });
    const handlers: [MediaSessionAction, () => void][] = [
      ['play', () => dispatch(play())],
      ['pause', () => dispatch(pause())],
      ['nexttrack', () => dispatch(next())],
      ['previoustrack', () => dispatch(prev())],
      ['seekbackward', () => { const a = audioRef.current; if (a) a.currentTime = Math.max(0, a.currentTime - 10); }],
      ['seekforward', () => { const a = audioRef.current; if (a) a.currentTime = Math.min(a.duration || 0, a.currentTime + 10); }],
    ];
    for (const [ev, fn] of handlers) {
      try { navigator.mediaSession.setActionHandler(ev, fn); } catch { /* not supported */ }
    }
    return () => {
      for (const [ev] of handlers) {
        try { navigator.mediaSession.setActionHandler(ev, null); } catch { /* noop */ }
      }
    };
  }, [track?.id, dispatch, track]);

  if (!track) return null;

  const pct = duration > 0 ? (progress / duration) * 100 : 0;

  return (
    <Box
      as="section"
      position="fixed"
      bottom={{ base: 'calc(68px + env(safe-area-inset-bottom, 0px))', md: 'calc(68px + env(safe-area-inset-bottom, 0px))' }}
      left={0}
      right={0}
      bg="rgba(15, 11, 31, 0.92)"
      borderTop="1px solid"
      borderColor="whiteAlpha.150"
      px={4}
      py={2}
      zIndex={14}
      backdropFilter="blur(18px) saturate(1.3)"
      boxShadow="0 -8px 32px rgba(0,0,0,0.45)"
    >
      <audio
        ref={audioRef}
        preload="metadata"
        onTimeUpdate={(e) => dispatch(setProgress(e.currentTarget.currentTime))}
        onLoadedMetadata={(e) => dispatch(setDuration(e.currentTarget.duration || 0))}
        onEnded={() => dispatch(onEnded())}
        onPlay={() => { dispatch(play()); clearAttempts(track?.id); }}
        onPause={() => {/* let Redux be authoritative */}}
        onError={handleAudioError}
      />
      <Flex maxW="1200px" mx="auto" align="center" gap={4}>
        {/* Cover + title — tapping opens the full-screen player */}
        <HStack
          as="button"
          spacing={3}
          minW={0}
          flex={{ base: '1', md: '0 0 260px' }}
          onClick={() => dispatch(expand())}
          aria-label="Open full player"
          textAlign="left"
          _hover={{ opacity: 0.9 }}
          _focus={{ outline: '2px solid', outlineColor: 'pink.400', borderRadius: '6px' }}
        >
          {track.coverUrl ? (
            <Image src={track.coverUrl} boxSize="44px" borderRadius="6px" objectFit="cover" alt="" />
          ) : (
            <Box boxSize="44px" borderRadius="6px" bg="pink.500" display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
              <Text fontSize="lg">🎵</Text>
            </Box>
          )}
          <Stack spacing={0} minW={0}>
            <Text color="whiteAlpha.900" fontSize="sm" fontWeight={700} noOfLines={1}>{track.title}</Text>
            <Text color="whiteAlpha.600" fontSize="xs" noOfLines={1}>{track.artist || 'MixGenie'}</Text>
          </Stack>
        </HStack>

        {/* Transport + seek */}
        <Stack spacing={1} flex="1" minW={0}>
          <HStack justify="center" spacing={1}>
            <IconButton
              aria-label="Previous"
              icon={<TransportIcon d="M6 6h2v12H6zM9.5 12l8.5 6V6z" />}
              size="sm"
              variant="ghost"
              color="whiteAlpha.800"
              isDisabled={index <= 0}
              onClick={() => dispatch(prev())}
            />
            <IconButton
              aria-label={isPlaying ? 'Pause' : 'Play'}
              icon={isPlaying ? <TransportIcon d="M6 5h4v14H6zM14 5h4v14h-4z" /> : <TransportIcon d="M8 5v14l11-7z" />}
              size="md"
              colorScheme="pink"
              borderRadius="full"
              onClick={() => dispatch(isPlaying ? pause() : play())}
            />
            <IconButton
              aria-label="Next"
              icon={<TransportIcon d="M6 18l8.5-6L6 6v12zM16 6h2v12h-2z" />}
              size="sm"
              variant="ghost"
              color="whiteAlpha.800"
              isDisabled={index >= queue.length - 1}
              onClick={() => dispatch(next())}
            />
          </HStack>
          <HStack spacing={2} color="whiteAlpha.600" fontSize="xs">
            <Text width="40px" textAlign="right" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(progress)}</Text>
            <Slider
              aria-label="Seek"
              value={pct}
              min={0}
              max={100}
              onChange={(v) => {
                if (!duration) return;
                const t = (v / 100) * duration;
                const a = audioRef.current;
                if (a) a.currentTime = t;
                dispatch(seek(t));
              }}
              flex="1"
              focusThumbOnChange={false}
            >
              <SliderTrack bg="whiteAlpha.300" h="3px">
                <SliderFilledTrack bg="pink.400" />
              </SliderTrack>
              <SliderThumb boxSize={3} bg="pink.400" />
            </Slider>
            <Text width="40px" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(duration)}</Text>
          </HStack>
        </Stack>

        {/* Volume — desktop only, hidden on very narrow */}
        <HStack spacing={2} display={{ base: 'none', md: 'flex' }} width="120px">
          <Text fontSize="xs" color="whiteAlpha.600">🔊</Text>
          <Slider
            aria-label="Volume"
            value={volume * 100}
            min={0}
            max={100}
            onChange={(v) => dispatch(setVolume(v / 100))}
            focusThumbOnChange={false}
          >
            <SliderTrack bg="whiteAlpha.300" h="3px">
              <SliderFilledTrack bg="whiteAlpha.700" />
            </SliderTrack>
            <SliderThumb boxSize={2.5} bg="whiteAlpha.900" />
          </Slider>
        </HStack>
      </Flex>
    </Box>
  );
}

function TransportIcon({ d }: { d: string }) {
  return (
    <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor" style={{ display: 'block' }}>
      <path d={d} />
    </svg>
  );
}

function fmt(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}
